import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  DialogContentText
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CollectionsBookmarkTwoToneIcon from '@mui/icons-material/CollectionsBookmarkTwoTone'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Base from '../Components/Base'
import { apiUrl } from '../Services/api'
import { useAuth } from '../hooks/useAuth'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '../constants'

function SeriesManagement() {
  const { token, isAuthenticated } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [seriesList, setSeriesList] = useState([])
  const [loading, setLoading] = useState(true)

  const [editDialog, setEditDialog] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState(false)
  const [selectedSeries, setSelectedSeries] = useState(null)
  const [newName, setNewName] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  const fetchSeries = useCallback(async () => {
    if (!isAuthenticated || !token) return
    setLoading(true)
    try {
      const response = await fetch(apiUrl('/books/get/series'), {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) throw new Error('Erreur lors du chargement des séries')
      const data = await response.json()
      setSeriesList(data.sort((a, b) => a.name.localeCompare(b.name)))
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [token, isAuthenticated, enqueueSnackbar])

  useEffect(() => {
    fetchSeries()
  }, [fetchSeries])

  const openEdit = (series) => {
    setSelectedSeries(series)
    setNewName(series.name)
    setEditDialog(true)
  }

  const openDelete = (series) => {
    setSelectedSeries(series)
    setDeleteDialog(true)
  }

  const handleEditSubmit = async () => {
    if (!newName.trim()) {
      enqueueSnackbar('Le nom ne peut pas être vide.', { variant: 'warning' })
      return
    }
    setActionLoading(true)
    try {
      const response = await fetch(apiUrl(`/books/series/edit/${selectedSeries.id}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: newName.trim() })
      })

      if (!response.ok) {
        const errData = await response.json()
        throw new Error(errData.detail || 'Erreur lors de la modification.')
      }

      enqueueSnackbar('Série renommée avec succès.', { variant: 'success' })
      setEditDialog(false)
      fetchSeries()
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteSubmit = async () => {
    setActionLoading(true)
    try {
      const response = await fetch(apiUrl(`/books/series/delete/${selectedSeries.id}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const errData = await response.json()
        throw new Error(errData.detail || 'Erreur lors de la suppression.')
      }

      enqueueSnackbar('Série supprimée avec succès.', { variant: 'success' })
      setDeleteDialog(false)
      fetchSeries()
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <Base>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(ROUTES.HOME)} aria-label="Retour" color="primary">
          <ArrowBackIcon />
        </IconButton>
        <CollectionsBookmarkTwoToneIcon color="primary" sx={{ fontSize: 32, display: { xs: 'none', sm: 'block' } }} />
        <Typography variant="h4" fontWeight="bold">Gestion de mes Séries</Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : seriesList.length === 0 ? (
        <Typography variant="h6" color="text.secondary" sx={{ mt: 4 }}>
          Vous n'avez aucune série pour le moment.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {seriesList.map((series) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={series.id}>
              <Card
                elevation={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  border: '2px solid',
                  borderColor: 'divider',
                  transition: 'border-color 0.2s, transform 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-4px)'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {series.name}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                  <IconButton color="primary" onClick={() => openEdit(series)} aria-label="Renommer">
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => openDelete(series)} aria-label="Supprimer">
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => !actionLoading && setEditDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Renommer la série</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom de la série"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} disabled={actionLoading}>Annuler</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => !actionLoading && setDeleteDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Supprimer la série ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer la série <strong>{selectedSeries?.name}</strong> ?<br/><br/>
            Cela la retirera de tous les livres qui y sont associés, mais ne supprimera <strong>pas</strong> les livres eux-mêmes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={actionLoading}>Annuler</Button>
          <Button onClick={handleDeleteSubmit} color="error" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Base>
  )
}

export default SeriesManagement
